import {
  buildSearchAcknowledgement,
  buildSearchReply,
} from '@src/services/clipSearch/searchReply';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ClipFragment } from '@src/services/AIClipProducer';
import type {
  ClipSearchConfig,
  ClipSearchService,
  ClipSearchToken,
} from '@src/services/clipSearch/ClipSearchService';
import type { TranscriptAnswerService } from '@src/services/transcriptAnswer/TranscriptAnswerService';
import type { StoryVideo } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * What the composer will do with the next sentence. The user sets it on a
 * toggle rather than the model inferring it: "What almost went wrong" is a
 * question *and* a clip request, so no heuristic can separate the two, and a
 * wrong guess costs a wait measured in tens of seconds.
 */
export type ClipChatMode = 'clips' | 'ask';

export type ClipChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /**
   * `results` is the message the candidate cards render under, so the panel
   * knows which assistant turn owns the current result set. `answer` is a
   * streamed reply in Ask mode — it fills in place as chunks arrive. `error`
   * is styled as a problem rather than an answer.
   */
  kind?: 'text' | 'results' | 'answer' | 'error';
};

export interface UseClipSearchChatInput {
  service: ClipSearchService;
  answerService: TranscriptAnswerService;
  transcriptElements: TranscriptElement[] | undefined;
  transcriptionText: string;
  originalVideo: StoryVideo | null | undefined;
  originalDuration: number;
  config: ClipSearchConfig;
}

export interface UseClipSearchChatResult {
  messages: ClipChatMessage[];
  isSearching: boolean;
  /** The current search's results — empty until one completes. */
  candidates: ClipFragment[];
  /**
   * Identity of the current search, stable for as long as one question's
   * answer is on screen. Downstream state keys its resets on this rather than
   * on `candidates`, which changes identity with every streamed chunk.
   */
  searchId: string | null;
  mode: ClipChatMode;
  setMode: (mode: ClipChatMode) => void;
  /** Null when no length was requested ("Any"). */
  targetDuration: number | null;
  setTargetDuration: (seconds: number | null) => void;
  sendMessage: (text: string) => void;
  cancelSearch: () => void;
  reset: () => void;
}

let messageCounter = 0;
function createMessageId(): string {
  messageCounter += 1;
  return `clip-chat-${Date.now()}-${messageCounter}`;
}

/**
 * Chat state for the Find Clips tab (PLAT-11806).
 *
 * One turn = one search, or one answer. `ClipSearchService` and
 * `TranscriptAnswerService` do the work; this hook owns the transcript of the
 * conversation, the in-flight token and the current result set.
 *
 * The two modes remember differently, because their backends do. A **clip**
 * turn re-runs a full search from scratch, so refining across turns ("make
 * them shorter") is out of scope — a new request replaces the old answer
 * rather than narrowing it. An **answer** turn carries the previous exchange,
 * so "tell me more" works; one exchange is the whole of it, since that is what
 * the endpoint's message list holds.
 *
 * The two modes share one token, so switching or cancelling mid-flight stops
 * whichever is running; they do **not** share the result set — an Ask turn
 * leaves `candidates` and `searchId` untouched, so asking something while
 * reading a search doesn't wipe the marks out of the transcript.
 */
export function useClipSearchChat(
  input: UseClipSearchChatInput,
): UseClipSearchChatResult {
  const {
    service,
    answerService,
    transcriptElements,
    transcriptionText,
    originalVideo,
    originalDuration,
    config,
  } = input;

  const [messages, setMessages] = useState<ClipChatMessage[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [candidates, setCandidates] = useState<ClipFragment[]>([]);
  const [searchId, setSearchId] = useState<string | null>(null);
  /**
   * Asking opens first: it is the cheaper, more forgiving turn — a question
   * answers in a sentence and costs nothing if the phrasing was vague, while a
   * clip search spends a long wait before the user learns their ask was off.
   */
  const [mode, setMode] = useState<ClipChatMode>('ask');
  const [targetDuration, setTargetDuration] = useState<number | null>(null);

  const tokenRef = useRef<ClipSearchToken | null>(null);
  /**
   * The last question that got an answer, so the next one can be a follow-up.
   * Only one pair is kept because only one fits: the endpoint takes a single
   * prior exchange. Clip turns never write it — "3 options, 0:24 to 0:31" is
   * not context an answer can use.
   */
  const lastAnswerRef = useRef<{ question: string; answer: string } | null>(
    null,
  );

  // Abandon an in-flight search when the tab unmounts, so its completion can't
  // write into a panel that is gone.
  useEffect(
    () => () => {
      if (tokenRef.current) tokenRef.current.aborted = true;
    },
    [],
  );

  const cancelSearch = useCallback(() => {
    if (tokenRef.current) tokenRef.current.aborted = true;
    tokenRef.current = null;
    setIsSearching(false);
  }, []);

  const reset = useCallback(() => {
    cancelSearch();
    setMessages([]);
    setCandidates([]);
    setSearchId(null);
    lastAnswerRef.current = null;
  }, [cancelSearch]);

  const sendMessage = useCallback(
    (text: string) => {
      const prompt = text.trim();
      // The ref, not `isSearching`: a second send in the same tick (double
      // click, Enter + Send) still sees the pre-render state.
      if (!prompt || tokenRef.current) return;

      if (mode === 'ask') {
        const token: ClipSearchToken = {
          id: createMessageId(),
          aborted: false,
        };
        tokenRef.current = token;

        // The reply streams into this bubble as chunks land, so it is pushed
        // empty and filled by id. `candidates`/`searchId` are untouched: an
        // answer must not clear a result set the user is still reading.
        const answerId = createMessageId();
        setMessages(prev => [
          ...prev,
          { id: createMessageId(), role: 'user', content: prompt },
          { id: answerId, role: 'assistant', content: '', kind: 'answer' },
        ]);
        setIsSearching(true);

        const fillAnswer = (content: string, kind: ClipChatMessage['kind']) =>
          setMessages(prev =>
            prev.map(m => (m.id === answerId ? { ...m, content, kind } : m)),
          );

        void answerService
          .answer({
            question: prompt,
            transcriptionText,
            previousTurn: lastAnswerRef.current ?? undefined,
            token,
            onChunk: textSoFar => {
              if (token.aborted) return;
              fillAnswer(textSoFar, 'answer');
            },
          })
          .then(result => {
            // A cancelled turn keeps whatever streamed in — stopping should
            // not take back the half you already read.
            if (token.aborted || tokenRef.current !== token) return;
            if (!result.ok) {
              fillAnswer(result.error.message, 'error');
              return;
            }
            fillAnswer(result.value.text, 'answer');
            lastAnswerRef.current = {
              question: prompt,
              answer: result.value.text,
            };
          })
          .finally(() => {
            if (tokenRef.current === token) {
              tokenRef.current = null;
              setIsSearching(false);
            }
          });
        return;
      }

      if (!originalVideo) {
        setMessages(prev => [
          ...prev,
          { id: createMessageId(), role: 'user', content: prompt },
          {
            id: createMessageId(),
            role: 'assistant',
            content: 'This story has no video to cut clips from yet.',
            kind: 'error',
          },
        ]);
        return;
      }

      const token: ClipSearchToken = {
        id: createMessageId(),
        aborted: false,
      };
      tokenRef.current = token;
      setSearchId(token.id);

      // The acknowledgement goes up before the await: a search reads the whole
      // transcript in chunks, and a chat that sits silent for tens of seconds
      // reads as broken (PLAT-11806 DS8).
      setMessages(prev => [
        ...prev,
        { id: createMessageId(), role: 'user', content: prompt },
        {
          id: createMessageId(),
          role: 'assistant',
          content: buildSearchAcknowledgement(prompt),
        },
      ]);
      setCandidates([]);
      setIsSearching(true);

      void service
        .search({
          prompt,
          targetDuration,
          originalVideo,
          originalDuration,
          transcriptionElements: transcriptElements ?? [],
          transcriptionText,
          config,
          token,
          onPartialResults: partial => {
            if (token.aborted) return;
            // Stream them in as each chunk lands, so the list fills rather
            // than appearing all at once at the end.
            setCandidates(prev =>
              partial.length === prev.length ? prev : [...partial],
            );
          },
        })
        .then(result => {
          if (token.aborted || tokenRef.current !== token) return;
          if (!result.ok) {
            setCandidates([]);
            setMessages(prev => [
              ...prev,
              {
                id: createMessageId(),
                role: 'assistant',
                content: result.error.message,
                kind: 'error',
              },
            ]);
            return;
          }
          setCandidates(result.value.candidates);
          setMessages(prev => [
            ...prev,
            {
              id: createMessageId(),
              role: 'assistant',
              content: buildSearchReply(result.value.candidates),
              kind: 'results',
            },
          ]);
        })
        .finally(() => {
          if (tokenRef.current === token) {
            tokenRef.current = null;
            setIsSearching(false);
          }
        });
    },
    [
      service,
      answerService,
      mode,
      targetDuration,
      originalVideo,
      originalDuration,
      transcriptElements,
      transcriptionText,
      config,
    ],
  );

  return {
    messages,
    isSearching,
    candidates,
    searchId,
    mode,
    setMode,
    targetDuration,
    setTargetDuration,
    sendMessage,
    cancelSearch,
    reset,
  };
}
